import { z } from 'zod';
import { baseDocumentFields, objectIdString, sha256Hex } from './common.js';
import { metaActionSource, metaEventName } from './enums.js';

// Meta CAPI accepts arrays for hashable fields (em, ph, fn, ln). We always
// send a single-element array.
export const metaUserData = z
  .object({
    em: z.array(sha256Hex).optional(),
    ph: z.array(sha256Hex).optional(),
    fn: z.array(sha256Hex).optional(),
    ln: z.array(sha256Hex).optional(),
    fbp: z.string().optional(),
    fbc: z.string().optional(),
    client_ip_address: z.string().optional(),
    client_user_agent: z.string().optional(),
  })
  .strict();

export type MetaUserData = z.infer<typeof metaUserData>;

// Custom data varies by event · Meta accepts arbitrary fields. We keep a
// small typed subset and allow passthrough for the rest.
export const metaCustomData = z
  .object({
    value: z.number().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    content_name: z.string().optional(),
    content_category: z.string().optional(),
    content_ids: z.array(z.string()).optional(),
  })
  .passthrough();

export type MetaCustomData = z.infer<typeof metaCustomData>;

export const metaEventResponse = z
  .object({
    success: z.boolean(),
    fbtraceId: z.string().optional(),
    eventsReceived: z.number().int().optional(),
    error: z
      .object({
        message: z.string(),
        code: z.union([z.string(), z.number()]).optional(),
        type: z.string().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type MetaEventResponse = z.infer<typeof metaEventResponse>;

export const metaEvent = z
  .object({
    ...baseDocumentFields,
    /** Some events (e.g., Purchase fired from admin) are tied to a lead; others aren't. */
    leadId: objectIdString.optional(),
    /** Generated server-side (UUID v4). Passed to the browser pixel as `eventID` for dedup. */
    eventId: z.string().uuid(),
    eventName: metaEventName,
    /** Unix timestamp in SECONDS (not milliseconds). */
    eventTime: z.number().int().positive(),
    eventSourceUrl: z.string().url().optional(),
    actionSource: metaActionSource,
    userData: metaUserData,
    customData: metaCustomData.optional(),
    pixelId: z.string().min(1),
    sentAt: z.coerce.date(),
    response: metaEventResponse,
    /** Number of attempts made to send this event (>=1). */
    attempts: z.number().int().min(1),
  })
  .strict();

export type MetaEvent = z.infer<typeof metaEvent>;
